import React, { useMemo } from "react";
import { User, Trash2, Mail, MessageSquare, ArrowDownLeft, ArrowUpRight } from "lucide-react";
import AgentNotesSummary from "./AgentNotesSummary";
import {
  mergeNotesAndComms,
  commsToSummaryNotes,
  channelLabel,
  directionLabel,
} from "./commsTimeline";
import { useLinkedNotes, LinkedNoteCard, linkedToSummaryNotes } from "./linkedNotes";

// The "Agent Notes" right-hand panel, shared by both property detail pages.
// Shows the manual notes AND the property owner's email/SMS history, interleaved
// newest-first, with the AI summary fed by both (Paul Jimoulis, DOMA 13234622777
// / CRM-2026-00102: "add email/text communication to agent notes section to
// follow"). Comms are read-only; only manual notes can be deleted.
//
// Pass `propertyId` and the notes about the same seller from the rest of the
// CRM (Database row, Seller Lead, contact) are interleaved too, read-only, so
// an appraisal opened later still shows the whole history (Genneva Smarrelli,
// CRM-2026-00106).
export default function AgentNotesPanel({
  notes = [],
  comms = [],
  loading = false,
  subject = "",
  onDelete,
  propertyId,
}) {
  const linked = useLinkedNotes(
    propertyId ? { property: propertyId, from: "property" } : {},
    notes.length
  );
  const merged = useMemo(() => {
    const items = mergeNotesAndComms(notes, comms);
    if (!linked.length) return items;
    const ts = (v) => {
      const t = v ? new Date(v).getTime() : NaN;
      return Number.isFinite(t) ? t : -Infinity;
    };
    return [
      ...items,
      ...linked.map((n) => ({ kind: "linked", key: `linked-${n.key}`, ts: ts(n.created_at), data: n })),
    ].sort((a, b) => b.ts - a.ts);
  }, [notes, comms, linked]);

  const summaryNotes = useMemo(
    () => [
      ...notes.map((n) => ({
        body: n.content,
        created_at: n.created_at,
        author: noteAuthorName(n),
      })),
      ...commsToSummaryNotes(comms),
      ...linkedToSummaryNotes(linked),
    ],
    [notes, comms, linked]
  );

  const total = notes.length + comms.length + linked.length;

  return (
    <div className="bg-gray-50 rounded-xl p-6 border border-gray-100 h-[600px] flex flex-col">
      <h3 className="text-lg font-bold text-[#374151] mb-4 flex items-center gap-2">
        <span>Agent Notes</span>
        <span className="bg-gray-200 text-gray-600 text-[10px] px-2 py-0.5 rounded-full">
          {total}
        </span>
      </h3>

      {!loading && (
        <AgentNotesSummary notes={summaryNotes} subject={subject} className="mb-4" />
      )}

      <div className="flex-1 overflow-y-auto pr-2 space-y-4 custom-scrollbar">
        {loading ? (
          <div className="text-center py-10 text-gray-400 text-sm">Loading notes...</div>
        ) : total === 0 ? (
          <div className="text-center py-10 text-gray-400 text-sm italic">
            No notes or communications yet.
          </div>
        ) : (
          merged.map((item) =>
            item.kind === "comm" ? (
              <CommCard key={item.key} comm={item.data} />
            ) : item.kind === "linked" ? (
              <LinkedNoteCard key={item.key} note={item.data} />
            ) : (
              <NoteCard key={item.key} note={item.data} onDelete={onDelete} />
            )
          )
        )}
      </div>
    </div>
  );
}

// The API sends `author` as a bare user id, with the user under
// `author_details` and the display name as `author_name`. Agents keep their
// name in `name` (first/last are mostly blank), so reading only first/last
// labelled every note "Unknown User" (CRM-2026-00106 screenshot).
export function noteAuthorName(note) {
  const d = note?.author_details || {};
  const fromName = note?.author_name && note.author_name !== "Unknown" ? note.author_name : "";
  return (
    fromName ||
    d.name ||
    [d.first_name, d.last_name].filter(Boolean).join(" ") ||
    note?.author?.name ||
    d.email ||
    ""
  );
}

function NoteCard({ note, onDelete }) {
  const authorName = noteAuthorName(note) || "Unknown User";
  return (
    <div className="bg-white p-4 rounded-lg border border-gray-100 shadow-sm group">
      <div className="flex justify-between items-start mb-2">
        <div className="flex items-center gap-2">
          {note.author_details?.profile_pic_url || note.author?.profile_image ? (
            <img
              src={note.author_details?.profile_pic_url || note.author.profile_image}
              alt=""
              className="w-6 h-6 rounded-full"
            />
          ) : (
            <div className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center">
              <User className="w-3 h-3 text-gray-400" />
            </div>
          )}
          <span className="text-xs font-bold text-[#374151]">{authorName}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-gray-400">
            {note.created_at ? new Date(note.created_at).toLocaleString() : ""}
          </span>
          {onDelete && (
            <button
              onClick={() => onDelete(note.id)}
              className="text-gray-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all p-1"
              title="Delete Note"
            >
              <Trash2 className="w-3 h-3" />
            </button>
          )}
        </div>
      </div>
      <p className="text-sm text-gray-600 whitespace-pre-wrap leading-relaxed">{note.content}</p>
    </div>
  );
}

const STATUS_TONE = {
  delivered: "text-green-600",
  sent: "text-gray-500",
  opened: "text-green-600",
  clicked: "text-green-600",
  failed: "text-red-500",
  bounced: "text-red-500",
  skipped: "text-amber-500",
};

function CommCard({ comm }) {
  const isSms = (comm.channel || "").toLowerCase() === "sms";
  const inbound = (comm.direction || "").toLowerCase() === "inbound";
  const Channel = isSms ? MessageSquare : Mail;
  const Dir = inbound ? ArrowDownLeft : ArrowUpRight;
  const who = comm.sent_by_name || (inbound ? comm.contact_name || "Owner" : "System");
  const status = (comm.status || "").toLowerCase();
  return (
    <div className="bg-white p-4 rounded-lg border-l-4 border-[#55C5D0] border border-gray-100 shadow-sm">
      <div className="flex justify-between items-start mb-2">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-full bg-[#55C5D0]/10 flex items-center justify-center">
            <Channel className="w-3 h-3 text-[#55C5D0]" />
          </div>
          <span className="text-xs font-bold text-[#374151] flex items-center gap-1">
            <Dir className="w-3 h-3 text-gray-400" />
            {channelLabel(comm)} {directionLabel(comm).toLowerCase()}
          </span>
          {who && <span className="text-[10px] text-gray-400">· {who}</span>}
        </div>
        <span className="text-[10px] text-gray-400">
          {comm.occurred_at ? new Date(comm.occurred_at).toLocaleString() : ""}
        </span>
      </div>
      <p className="text-sm text-gray-600 whitespace-pre-wrap leading-relaxed">
        {comm.subject_or_preview || <span className="italic text-gray-400">(no preview)</span>}
      </p>
      {status && (
        <div className="mt-2 text-[10px] font-semibold uppercase tracking-wide">
          <span className={STATUS_TONE[status] || "text-gray-400"}>{status}</span>
        </div>
      )}
    </div>
  );
}
